/**
 * Räumt LaTeX aus den Coach-Antworten.
 *
 * Gerechnet wird in dieser Anwendung in Klartext — so wie die Foliensätze und
 * der hinterlegte Wissensbestand es tun, und so, wie es auch vorgelesen werden
 * kann. Der Prompt sagt das; diese Funktion ist das Netz darunter, denn ein
 * Modell, das einmal in den Formelsatz rutscht, hinterlässt sonst rohes
 * `\[ … \]` auf dem Schirm. Sie greift außerdem rückwirkend: Gespräche, die
 * vor der Prompt-Regel entstanden sind, werden beim erneuten Öffnen
 * mitgeräumt.
 *
 * Bewusst kein Formelsatz: Deutsche Kommazahlen brauchen in LaTeX die
 * `{,}`-Krücke, die kein Modell zuverlässig setzt, und ein Rentenwert mal
 * Entgeltpunkt ist keine Mathematik, die Satz braucht.
 */

/** Einzelne Befehle, die in den Rechnungen des Coaches vorkommen. */
const BEFEHLE: [RegExp, string][] = [
  [/\\times/g, "×"],
  [/\\cdot/g, "·"],
  [/\\div/g, "÷"],
  [/\\pm/g, "±"],
  [/\\approx/g, "≈"],
  [/\\neq/g, "≠"],
  [/\\leq?\b/g, "≤"],
  [/\\geq?\b/g, "≥"],
  [/\\%/g, "%"],
  [/\\\$/g, "$"],
  // Geschützte und schmale Abstände werden zu einem gewöhnlichen Leerzeichen.
  [/\\[,;:! ]/g, " "],
  [/\\quad|\\qquad/g, "  "],
]

/** Wandelt den Inhalt einer Formel in lesbaren Klartext. */
function entlatex(inhalt: string): string {
  let text = inhalt

  // `{,}` ist die LaTeX-Krücke für das deutsche Dezimalkomma.
  text = text.replace(/\{,\}/g, ",").replace(/\{\.\}/g, ".")

  // `\text{…}`, `\mathrm{…}` und Verwandte tragen die Einheiten.
  text = text.replace(
    /\\(?:text|mathrm|mathit|mathbf|operatorname)\s*\{([^{}]*)\}/g,
    "$1",
  )

  // Brüche werden zur Division: `\frac{a}{b}` → `a / b`.
  text = text.replace(/\\d?frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g, "$1 / $2")

  for (const [muster, ersatz] of BEFEHLE) text = text.replace(muster, ersatz)

  // Was an Klammern und Befehlen übrig bleibt, fliegt raus statt stehen.
  text = text.replace(/\\left|\\right/g, "")
  text = text.replace(/\\[a-zA-Z]+/g, "")
  text = text.replace(/[{}]/g, "")

  return text.replace(/[ \t]+/g, " ").trim()
}

/**
 * Ersetzt LaTeX-Blöcke durch Klartext.
 *
 * Abgesetzte Formeln (`\[ … \]`, `$$ … $$`) werden zu einem Codeblock, damit
 * die Ausrichtung erhalten bleibt; eingebettete (`\( … \)`) zu Inline-Code.
 * Einzelne `$` bleiben unangetastet — sie sind in einem Beratungstext
 * wahrscheinlicher eine Währung als eine Formel.
 */
export function ohneLatex(text: string): string {
  if (!/\\\[|\\\(|\$\$/.test(text)) return text

  return text
    .replace(/\\\[([\s\S]*?)\\\]/g, (_, inhalt: string) => {
      const sauber = entlatex(inhalt)
      return sauber ? `\n\n\`\`\`\n${sauber}\n\`\`\`\n\n` : ""
    })
    .replace(/\$\$([\s\S]*?)\$\$/g, (_, inhalt: string) => {
      const sauber = entlatex(inhalt)
      return sauber ? `\n\n\`\`\`\n${sauber}\n\`\`\`\n\n` : ""
    })
    .replace(/\\\(([\s\S]*?)\\\)/g, (_, inhalt: string) => {
      const sauber = entlatex(inhalt)
      return sauber ? `\`${sauber}\`` : ""
    })
    .replace(/\n{3,}/g, "\n\n")
    .trim()
}
